import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api.js';
import { type CommitResult, type ImportSheet, importApi } from '../../lib/import-api.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { CommitSummary } from './commit-summary.js';
import { MappingStep } from './mapping-step.js';
import { PreviewStep } from './preview-step.js';
import { ClassifyStep, UploadStep } from './upload-step.js';
import { EMPTY_USER_STEP_STATE, type UserStepState, UsersStep } from './users-step.js';
import { clearResume, loadResume, saveResume } from './wizard-resume.js';

// Design-Spec 3.8 / Screen 4a: Stepper in der Kontextzeile (erledigt ✓ grün, aktiv orange),
// rechts der Hinweis "Danach ist das Tool die Quelle der Wahrheit" (IMP-1).

// Task 10 (#65): 'users' sitzt zwischen 'mapping' und 'preview' — die Nutzerzuordnung braucht das
// bestätigte Mapping (responsible_code-Spalte), muss aber VOR der Vorschau/dem Commit passiert
// sein (die Vorschau soll bereits die zugeordneten/gestubbten Nutzer widerspiegeln können).
export type WizardStep = 'upload' | 'classify' | 'mapping' | 'users' | 'preview' | 'commit';
const STEPS: WizardStep[] = ['upload', 'classify', 'mapping', 'users', 'preview', 'commit'];

export interface WizardState {
  jobId: string | null;
  sheets: ImportSheet[];
  step: WizardStep;
  result?: CommitResult;
  // #73 (Review I1): Auswahl des Nutzer-Schritts — überlebt „Zurück“ aus der Vorschau. Ein neuer
  // Upload oder „Neuen Import beginnen“ setzt den ganzen Zustand neu und leert sie damit.
  users?: UserStepState;
}

function Stepper({ step }: { step: WizardStep }) {
  const { t } = useTranslation();
  const activeIdx = STEPS.indexOf(step);
  return (
    <ol className="flex items-center gap-4 text-xs">
      {STEPS.map((s, idx) => {
        const done = idx < activeIdx;
        const active = idx === activeIdx;
        const tone = done
          ? 'text-status-pass'
          : active
            ? 'text-brand-accent font-bold'
            : 'text-text-muted';
        return (
          <li key={s} className={`flex items-center gap-1 uppercase tracking-wide ${tone}`}>
            <span aria-hidden="true">{done ? '✓' : `${idx + 1}.`}</span>
            <span aria-current={active ? 'step' : undefined}>{t(`import.steps.${s}`)}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function ImportPage() {
  const { t } = useTranslation();
  const { project, isLoading } = useCurrentProject();
  const queryClient = useQueryClient();
  const [state, setState] = useState<WizardState>({ jobId: null, sheets: [], step: 'upload' });
  // #73: true, bis der Wiedereinstieg geprüft ist — sonst blitzt kurz der Upload-Schritt auf.
  const [checkingResume, setCheckingResume] = useState(true);
  const [resumedFilename, setResumedFilename] = useState<string | null>(null);
  const projectId = project?.project_id;
  // Stabil, weil UsersStep es als Effekt-Abhängigkeit nutzt.
  const updateUsers = useCallback(
    (update: (prev: UserStepState) => UserStepState) =>
      setState((s) => ({ ...s, users: update(s.users ?? EMPTY_USER_STEP_STATE) })),
    [],
  );

  // #73: Beim Mount den gemerkten Import dieses Tabs wieder aufnehmen. Der Upload-Schritt selbst
  // (laufende Klassifikation vor „Weiter“) wird bewusst nicht wiederhergestellt: ein erneuter
  // Upload kostet Sekunden, ein verlorener Entwurf Minuten.
  useEffect(() => {
    if (!projectId) return;
    const entry = loadResume(projectId);
    if (!entry) {
      setCheckingResume(false);
      return;
    }
    let cancelled = false;
    importApi
      .getJob(projectId, entry.jobId)
      .then(({ job, sheets }) => {
        if (cancelled) return;
        if (job.status === 'committed') {
          clearResume(projectId);
        } else {
          setState({ jobId: entry.jobId, sheets, step: entry.step });
          setResumedFilename(job.filename);
        }
      })
      .catch((err: unknown) => {
        // Nur "Vorgang weg / kein Zugriff" macht den Eintrag wertlos. Bei Netz- oder Serverfehlern
        // bleibt er: der Nutzer sieht den Upload, kann aber beim nächsten Besuch wieder einsteigen.
        if (!cancelled && err instanceof ApiError && (err.status === 404 || err.status === 403))
          clearResume(projectId);
      })
      .finally(() => {
        if (!cancelled) setCheckingResume(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // #73: Jeden Schrittwechsel ab 'classify' merken; nach dem Commit ist der Import erledigt.
  // 'upload' löscht bewusst nicht: der Anfangszustand würde sonst vor der Wiederherstellung
  // den Eintrag zerstören.
  useEffect(() => {
    if (!projectId || checkingResume) return;
    if (state.step === 'commit') clearResume(projectId);
    else if (state.step !== 'upload' && state.jobId)
      saveResume(projectId, { jobId: state.jobId, step: state.step });
  }, [projectId, checkingResume, state.step, state.jobId]);

  if (isLoading) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  if (!project) return <div className="p-6 text-text-muted">{t('import.noProject')}</div>;
  if (checkingResume) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;

  return (
    <div className="flex flex-col">
      <div className="flex h-[44px] items-center justify-between border-b border-[var(--border)] bg-bg-surface px-6">
        <Stepper step={state.step} />
        <span className="text-[11px] text-text-muted">{t('import.sourceOfTruthHint')}</span>
      </div>
      <div className="p-6">
        <h1 className="mb-4 text-base font-bold text-text-primary">{t('import.title')}</h1>
        {resumedFilename && state.step !== 'commit' && (
          <div className="mb-4 flex items-center justify-between rounded-card border border-[var(--border)] bg-bg-surface px-4 py-2 text-xs text-text-muted">
            <span>{t('import.resume.banner', { filename: resumedFilename })}</span>
            <button
              type="button"
              className="font-bold text-brand-accent hover:underline"
              onClick={() => {
                clearResume(project.project_id);
                setResumedFilename(null);
                setState({ jobId: null, sheets: [], step: 'upload' });
              }}
            >
              {t('import.resume.discard')}
            </button>
          </div>
        )}
        {state.step === 'upload' && (
          <UploadStep
            projectId={project.project_id}
            onUploaded={(jobId, sheets) => {
              setResumedFilename(null);
              setState({ jobId, sheets, step: 'classify' });
            }}
          />
        )}
        {state.step === 'classify' && state.jobId && (
          <ClassifyStep
            projectId={project.project_id}
            jobId={state.jobId}
            sheets={state.sheets}
            onConfirmed={(sheets) => setState({ ...state, sheets, step: 'mapping' })}
          />
        )}
        {state.step === 'mapping' && state.jobId && (
          <MappingStep
            projectId={project.project_id}
            jobId={state.jobId}
            sheets={state.sheets}
            onConfirmed={() => setState({ ...state, step: 'users' })}
          />
        )}
        {state.step === 'users' && state.jobId && (
          <UsersStep
            projectId={project.project_id}
            jobId={state.jobId}
            state={state.users ?? EMPTY_USER_STEP_STATE}
            onStateChange={updateUsers}
            // Funktional: onConfirmed folgt direkt auf das Merken der gespeicherten Auswahl.
            onConfirmed={() => setState((s) => ({ ...s, step: 'preview' }))}
          />
        )}
        {state.step === 'preview' && state.jobId && (
          <PreviewStep
            projectId={project.project_id}
            jobId={state.jobId}
            onCommitted={(result) => {
              // #143: Der Commit legt Prozesse und Testfälle an — der gecachte Katalogbaum und die
              // Volliste des Filtermodus kennen sie noch nicht (bis zu 30 s veraltet).
              void queryClient.invalidateQueries({
                queryKey: ['catalog-tree', project.project_id],
              });
              void queryClient.invalidateQueries({
                queryKey: ['tcs-search', project.project_id],
              });
              setState({ ...state, step: 'commit', result });
            }}
            onBack={() => setState((s) => ({ ...s, step: 'users' }))}
            // #151: direkter Rücksprung in die Spaltenzuordnung (gespeichertes Mapping wird dort
            // über PATCH .../sheets/:sheetId wieder angezeigt, ohne den Entwurf zu verwerfen).
            onBackToMapping={() => setState((s) => ({ ...s, step: 'mapping' }))}
          />
        )}
        {state.step === 'commit' && state.result && (
          <div className="max-w-xl rounded-card border border-[var(--border)] bg-bg-surface p-6">
            <CommitSummary result={state.result} />
            <p className="mt-2 text-xs text-text-muted">{t('import.done.hint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
